import { createTheme } from '@mui/material/styles';
import { brandCss } from './brandCss';
import { brandColors } from './brandColors';

/** Global border radius — 4px across the system */
export const RADIUS = 4;

/**
 * Inter type scale — production SaaS dashboard
 * Page title 28/700/36 · Section 22/600/30 · Card 18/600/26
 * Body 14/400/22 · Secondary 13/400/20 · Helper 12/400/18
 * Button 14/600/20 · Nav 14/500/20 · Table head 12/600 · Table cell 13
 */
export const TYPE = {
  pageTitle: { fontSize: 28, fontWeight: 700, lineHeight: '36px' },
  sectionHeading: { fontSize: 22, fontWeight: 600, lineHeight: '30px' },
  cardHeading: { fontSize: 18, fontWeight: 600, lineHeight: '26px' },
  body: { fontSize: 14, fontWeight: 400, lineHeight: '22px' },
  secondary: { fontSize: 13, fontWeight: 400, lineHeight: '20px' },
  helper: { fontSize: 12, fontWeight: 400, lineHeight: '18px' },
  button: { fontSize: 14, fontWeight: 600, lineHeight: '20px' },
  nav: { fontSize: 14, fontWeight: 500, lineHeight: '20px' },
  tableHeader: { fontSize: 11, fontWeight: 600, lineHeight: '16px' },
  tableContent: { fontSize: 12, fontWeight: 400, lineHeight: '18px' },
  fieldLabel: { fontSize: 14, fontWeight: 500, lineHeight: '20px' },
  fieldInput: { fontSize: 14, fontWeight: 400, lineHeight: '20px' },
} as const;

/** Form control heights */
export const INPUT_HEIGHT = 44;
export const INPUT_HEIGHT_DENSE = 40;
/** Table filter / search bar — compact */
export const INPUT_HEIGHT_COMPACT = 32;
export const INPUT_HEIGHT_AUTH = 48;
export const INPUT_PX = 14;

/** @deprecated Prefer TYPE.body.fontSize — kept for existing imports */
export const BASE_FONT_SIZE = TYPE.body.fontSize;

/** Single typeface for the entire tenant + auth UI */
export const APP_FONT_FAMILY =
  '"Inter", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';

export { brandColors } from './brandColors';
export { brandCss, activeStatusColors } from './brandCss';

/** Shared outlined-input look — desktop CRM default 44px */
const cleanInputRoot = {
  borderRadius: RADIUS,
  fontSize: TYPE.fieldInput.fontSize,
  fontFamily: APP_FONT_FAMILY,
  fontWeight: TYPE.fieldInput.fontWeight,
  backgroundColor: '#fff',
  height: INPUT_HEIGHT,
  minHeight: INPUT_HEIGHT,
  boxSizing: 'border-box' as const,
  transition: 'border-color 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease',
  '& .MuiOutlinedInput-input': {
    fontSize: TYPE.fieldInput.fontSize,
    fontFamily: APP_FONT_FAMILY,
    fontWeight: TYPE.fieldInput.fontWeight,
    lineHeight: TYPE.fieldInput.lineHeight,
    padding: `0 ${INPUT_PX}px`,
    height: INPUT_HEIGHT,
    boxSizing: 'border-box' as const,
    '&::placeholder': {
      fontSize: TYPE.fieldInput.fontSize,
      fontWeight: 400,
      opacity: 0.75,
      color: brandColors.textMuted,
    },
  },
  '& textarea.MuiOutlinedInput-input': {
    padding: `12px ${INPUT_PX}px`,
    lineHeight: TYPE.fieldInput.lineHeight,
    height: 'auto',
  },
  '&.MuiInputBase-multiline': {
    height: 'auto',
    minHeight: 88,
    alignItems: 'flex-start',
  },
  '& .MuiSelect-select': {
    fontSize: TYPE.fieldInput.fontSize,
    fontFamily: APP_FONT_FAMILY,
    fontWeight: TYPE.fieldInput.fontWeight,
    lineHeight: TYPE.fieldInput.lineHeight,
    padding: `0 ${INPUT_PX}px`,
    height: INPUT_HEIGHT,
    display: 'flex',
    alignItems: 'center',
    boxSizing: 'border-box' as const,
  },
  '& fieldset': {
    borderColor: brandColors.border,
    borderWidth: 1.5,
  },
  '&:hover fieldset': {
    borderColor: brandCss.primaryLight,
  },
  '&.Mui-focused': {
    boxShadow: brandCss.focusRing,
    '& fieldset': {
      borderColor: brandCss.primary,
      borderWidth: 2,
    },
  },
  '&.Mui-error fieldset': {
    borderColor: brandColors.danger,
  },
  '&.Mui-disabled': {
    backgroundColor: '#f8fafc',
  },
};

export const appTheme = createTheme({
  palette: {
    mode: 'light',
    primary: { main: brandColors.primary, dark: brandColors.primaryDark, light: brandColors.primaryLight, contrastText: '#ffffff' },
    secondary: { main: brandColors.secondary },
    success: { main: brandColors.success },
    warning: { main: brandColors.warning },
    error: { main: brandColors.danger },
    info: { main: brandColors.info },
    background: { default: brandColors.contentBg, paper: brandColors.cardBg },
    text: { primary: brandColors.textDark, secondary: brandColors.textMuted },
  },
  typography: {
    fontFamily: APP_FONT_FAMILY,
    fontSize: TYPE.body.fontSize,
    htmlFontSize: 16,
    h4: {
      fontFamily: APP_FONT_FAMILY,
      fontSize: TYPE.pageTitle.fontSize,
      fontWeight: TYPE.pageTitle.fontWeight,
      lineHeight: TYPE.pageTitle.lineHeight,
    },
    h5: {
      fontFamily: APP_FONT_FAMILY,
      fontSize: TYPE.sectionHeading.fontSize,
      fontWeight: TYPE.sectionHeading.fontWeight,
      lineHeight: TYPE.sectionHeading.lineHeight,
    },
    h6: {
      fontFamily: APP_FONT_FAMILY,
      fontSize: TYPE.cardHeading.fontSize,
      fontWeight: TYPE.cardHeading.fontWeight,
      lineHeight: TYPE.cardHeading.lineHeight,
    },
    body1: {
      fontFamily: APP_FONT_FAMILY,
      fontSize: TYPE.body.fontSize,
      fontWeight: TYPE.body.fontWeight,
      lineHeight: TYPE.body.lineHeight,
    },
    body2: {
      fontFamily: APP_FONT_FAMILY,
      fontSize: TYPE.secondary.fontSize,
      fontWeight: TYPE.secondary.fontWeight,
      lineHeight: TYPE.secondary.lineHeight,
    },
    button: {
      fontFamily: APP_FONT_FAMILY,
      fontSize: TYPE.button.fontSize,
      fontWeight: TYPE.button.fontWeight,
      lineHeight: TYPE.button.lineHeight,
      textTransform: 'none',
    },
    subtitle1: {
      fontFamily: APP_FONT_FAMILY,
      fontSize: TYPE.cardHeading.fontSize,
      fontWeight: TYPE.cardHeading.fontWeight,
      lineHeight: TYPE.cardHeading.lineHeight,
    },
    subtitle2: {
      fontFamily: APP_FONT_FAMILY,
      fontSize: TYPE.secondary.fontSize,
      fontWeight: 500,
      lineHeight: TYPE.secondary.lineHeight,
      color: brandColors.textMuted,
    },
    caption: {
      fontFamily: APP_FONT_FAMILY,
      fontSize: TYPE.helper.fontSize,
      fontWeight: TYPE.helper.fontWeight,
      lineHeight: TYPE.helper.lineHeight,
    },
    overline: {
      fontFamily: APP_FONT_FAMILY,
      fontSize: TYPE.helper.fontSize,
      fontWeight: 500,
      lineHeight: TYPE.helper.lineHeight,
      letterSpacing: '0.06em',
    },
  },
  shape: { borderRadius: RADIUS },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        html: { fontSize: '16px' },
        body: {
          fontSize: TYPE.body.fontSize,
          lineHeight: TYPE.body.lineHeight,
          fontFamily: APP_FONT_FAMILY,
          fontWeight: TYPE.body.fontWeight,
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
        },
        'button, input, textarea, select, td, th, label': {
          fontFamily: APP_FONT_FAMILY,
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: { fontFamily: APP_FONT_FAMILY },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: brandCss.shadowMd,
          border: `1px solid ${brandColors.border}`,
          borderRadius: RADIUS,
        },
      },
    },
    MuiPaper: {
      styleOverrides: { root: { borderRadius: RADIUS, fontFamily: APP_FONT_FAMILY } },
    },
    MuiDialog: {
      styleOverrides: { paper: { borderRadius: RADIUS } },
    },
    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: RADIUS, fontSize: TYPE.secondary.fontSize },
        message: { fontSize: TYPE.secondary.fontSize, lineHeight: TYPE.secondary.lineHeight },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: RADIUS,
          fontSize: TYPE.helper.fontSize,
          fontFamily: APP_FONT_FAMILY,
          height: 26,
        },
        label: { fontSize: TYPE.helper.fontSize, px: 1 },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: TYPE.button.fontWeight,
          borderRadius: RADIUS,
          fontSize: TYPE.button.fontSize,
          lineHeight: TYPE.button.lineHeight,
          fontFamily: APP_FONT_FAMILY,
          minHeight: INPUT_HEIGHT,
        },
        sizeSmall: {
          minHeight: INPUT_HEIGHT_DENSE,
          fontSize: TYPE.button.fontSize,
          lineHeight: TYPE.button.lineHeight,
        },
        sizeLarge: {
          minHeight: INPUT_HEIGHT_AUTH,
          fontSize: TYPE.button.fontSize,
        },
        containedPrimary: {
          color: '#fff',
          background: brandCss.gradientPrimary,
          boxShadow: brandCss.shadowButton,
          '&:hover': { background: brandCss.gradientPrimaryHover, color: '#fff' },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: brandColors.border,
          padding: 6,
          '&.Mui-checked': { color: brandCss.primary },
          '&.MuiCheckbox-indeterminate': { color: brandCss.primary },
        },
      },
      defaultProps: { size: 'small' },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        root: { marginLeft: -4, alignItems: 'center' },
        label: {
          fontSize: TYPE.body.fontSize,
          fontFamily: APP_FONT_FAMILY,
          lineHeight: TYPE.body.lineHeight,
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          background: `var(--brand-table-header, ${brandCss.primary})`,
          '& .MuiTableCell-head': {
            fontWeight: 700,
            fontSize: 11,
            lineHeight: '16px',
            fontFamily: APP_FONT_FAMILY,
            textTransform: 'none',
            letterSpacing: '0.02em',
            color: 'var(--brand-table-header-text, #ffffff)',
            backgroundColor: `var(--brand-table-header, ${brandCss.primary})`,
            py: 1.25,
            px: 1.25,
            borderBottom: 'none',
          },
        },
      },
    },
    MuiTableSortLabel: {
      styleOverrides: {
        root: {
          color: 'inherit',
          fontWeight: 700,
          fontSize: 11,
          lineHeight: '16px',
          fontFamily: APP_FONT_FAMILY,
          '&:hover': { color: 'inherit' },
          '&.Mui-active': {
            color: 'inherit',
            '& .MuiTableSortLabel-icon': { color: 'inherit', opacity: 1 },
          },
          '& .MuiTableSortLabel-icon': {
            fontSize: 14,
            opacity: 0,
            color: 'inherit',
          },
          '&:hover .MuiTableSortLabel-icon': {
            opacity: 0.85,
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontSize: TYPE.tableContent.fontSize,
          fontFamily: APP_FONT_FAMILY,
          fontWeight: 500,
          py: 1.25,
          px: 1.25,
          lineHeight: TYPE.tableContent.lineHeight,
          color: brandColors.textDark,
        },
        head: {
          backgroundColor: `var(--brand-table-header, ${brandCss.primary})`,
          color: 'var(--brand-table-header-text, #ffffff)',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: TYPE.body.fontSize,
          fontFamily: APP_FONT_FAMILY,
          lineHeight: TYPE.body.lineHeight,
          minHeight: INPUT_HEIGHT_DENSE,
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        primary: {
          fontSize: TYPE.nav.fontSize,
          fontFamily: APP_FONT_FAMILY,
          fontWeight: TYPE.nav.fontWeight,
          lineHeight: TYPE.nav.lineHeight,
        },
        secondary: {
          fontSize: TYPE.helper.fontSize,
          fontFamily: APP_FONT_FAMILY,
          lineHeight: TYPE.helper.lineHeight,
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          fontFamily: APP_FONT_FAMILY,
          fontSize: TYPE.nav.fontSize,
          fontWeight: TYPE.nav.fontWeight,
          lineHeight: TYPE.nav.lineHeight,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          fontSize: TYPE.nav.fontSize,
          textTransform: 'none',
          fontWeight: TYPE.nav.fontWeight,
          fontFamily: APP_FONT_FAMILY,
          lineHeight: TYPE.nav.lineHeight,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontSize: TYPE.helper.fontSize,
          fontFamily: APP_FONT_FAMILY,
          lineHeight: TYPE.helper.lineHeight,
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          fontSize: TYPE.helper.fontSize,
          fontFamily: APP_FONT_FAMILY,
          fontWeight: TYPE.helper.fontWeight,
          marginLeft: 2,
          marginTop: 6,
          lineHeight: TYPE.helper.lineHeight,
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          fontSize: TYPE.fieldInput.fontSize,
          fontFamily: APP_FONT_FAMILY,
          fontWeight: TYPE.fieldInput.fontWeight,
          lineHeight: TYPE.fieldInput.lineHeight,
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontSize: TYPE.cardHeading.fontSize,
          fontWeight: TYPE.cardHeading.fontWeight,
          lineHeight: TYPE.cardHeading.lineHeight,
          fontFamily: APP_FONT_FAMILY,
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          fontSize: TYPE.body.fontSize,
          lineHeight: TYPE.body.lineHeight,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: cleanInputRoot,
        input: {
          fontSize: TYPE.fieldInput.fontSize,
          fontFamily: APP_FONT_FAMILY,
          fontWeight: TYPE.fieldInput.fontWeight,
          lineHeight: TYPE.fieldInput.lineHeight,
          '&::placeholder': {
            color: brandColors.textMuted,
            opacity: 0.75,
            fontSize: TYPE.fieldInput.fontSize,
            fontWeight: 400,
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          fontSize: TYPE.fieldInput.fontSize,
          fontFamily: APP_FONT_FAMILY,
        },
        input: {
          fontSize: TYPE.fieldInput.fontSize,
          fontFamily: APP_FONT_FAMILY,
          fontWeight: TYPE.fieldInput.fontWeight,
          lineHeight: TYPE.fieldInput.lineHeight,
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        size: 'small',
        variant: 'outlined',
        InputLabelProps: { shrink: true },
      },
      styleOverrides: {
        root: {
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          '& .MuiOutlinedInput-root': cleanInputRoot,
          '& .MuiInputLabel-root': {
            position: 'relative',
            transform: 'none',
            marginBottom: 6,
            marginLeft: 0,
            maxWidth: '100%',
            fontSize: TYPE.fieldLabel.fontSize,
            fontFamily: APP_FONT_FAMILY,
            fontWeight: TYPE.fieldLabel.fontWeight,
            lineHeight: TYPE.fieldLabel.lineHeight,
            color: brandColors.textDark,
            overflow: 'visible',
            whiteSpace: 'normal',
          },
          '& .MuiInputLabel-root.Mui-focused': {
            color: brandCss.primaryDark,
          },
          '& .MuiInputLabel-asterisk': {
            color: brandColors.danger,
          },
          '& .MuiOutlinedInput-notchedOutline legend': {
            width: '0 !important',
            maxWidth: 0,
            padding: 0,
            '& span': { display: 'none' },
          },
          '& .MuiInputLabel-shrink': {
            transform: 'none',
          },
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          '& > .MuiInputLabel-root': {
            position: 'relative',
            transform: 'none',
            marginBottom: 6,
            fontSize: TYPE.fieldLabel.fontSize,
            fontFamily: APP_FONT_FAMILY,
            fontWeight: TYPE.fieldLabel.fontWeight,
            lineHeight: TYPE.fieldLabel.lineHeight,
            color: brandColors.textDark,
            maxWidth: '100%',
          },
          '& .MuiOutlinedInput-notchedOutline legend': {
            width: '0 !important',
            maxWidth: 0,
            padding: 0,
            '& span': { display: 'none' },
          },
        },
      },
    },
    MuiInputLabel: {
      defaultProps: {
        shrink: true,
      },
      styleOverrides: {
        root: {
          fontSize: TYPE.fieldLabel.fontSize,
          fontFamily: APP_FONT_FAMILY,
          fontWeight: TYPE.fieldLabel.fontWeight,
          lineHeight: TYPE.fieldLabel.lineHeight,
          '&.Mui-focused': { color: brandCss.primaryDark },
        },
        shrink: {
          transform: 'none',
          fontWeight: TYPE.fieldLabel.fontWeight,
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          fontSize: TYPE.fieldLabel.fontSize,
          fontFamily: APP_FONT_FAMILY,
          fontWeight: TYPE.fieldLabel.fontWeight,
          lineHeight: TYPE.fieldLabel.lineHeight,
        },
      },
    },
    MuiSnackbar: {
      styleOverrides: {
        root: { '& .MuiAlert-root': { borderRadius: RADIUS, boxShadow: brandCss.shadowLg } },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        root: {
          fontSize: TYPE.secondary.fontSize,
          fontFamily: APP_FONT_FAMILY,
        },
        selectLabel: { fontSize: TYPE.secondary.fontSize },
        displayedRows: { fontSize: TYPE.secondary.fontSize },
        select: { fontSize: TYPE.secondary.fontSize },
      },
    },
  },
});
